import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CONNECTION_TYPES,
  CONNECTION_TYPE_LABELS,
  DEFAULT_PORTS,
  type ConnectionType,
} from "./connectionTypes";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  connection_type: z.enum(CONNECTION_TYPES),
  host: z.string().min(1, "Host is required"),
  port: z.coerce.number().int().positive().optional(),
  model: z.string().optional(),
  management_url: z
    .string()
    .url("Must be a valid URL")
    .optional()
    .or(z.literal("")),
  enabled: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

interface StudioConnectionFormProps {
  onSubmit: (data: FormValues) => void;
  onCancel: () => void;
  isLoading?: boolean;
  initialValues?: Partial<FormValues>;
  submitLabel?: string;
}

export const StudioConnectionForm = ({
  onSubmit,
  onCancel,
  isLoading,
  initialValues,
  submitLabel,
}: StudioConnectionFormProps) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: initialValues?.name ?? "",
      connection_type: initialValues?.connection_type ?? "generic",
      host: initialValues?.host ?? "",
      port: initialValues?.port ?? undefined,
      model: initialValues?.model ?? "",
      management_url: initialValues?.management_url ?? "",
      enabled: initialValues?.enabled ?? true,
    },
  });

  const connectionType = form.watch("connection_type");
  const defaultPort = DEFAULT_PORTS[connectionType as ConnectionType];
  const isGeneric = connectionType === "generic";

  const handleSubmit = (values: FormValues) => {
    onSubmit({
      ...values,
      port: isGeneric ? undefined : values.port || defaultPort,
      model: values.model || undefined,
      management_url: values.management_url || undefined,
    });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-4"
        data-testid="device-form"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name *</FormLabel>
              <FormControl>
                <Input
                  placeholder="e.g., Network Switch"
                  data-testid="device-name-input"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="connection_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Device Type *</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger data-testid="device-type-select">
                    <SelectValue placeholder="Select device type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {CONNECTION_TYPES.map((type) => (
                    <SelectItem
                      key={type}
                      value={type}
                      data-testid={`device-type-option-${type}`}
                    >
                      {CONNECTION_TYPE_LABELS[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className={isGeneric ? "" : "grid grid-cols-2 gap-4"}>
          <FormField
            control={form.control}
            name="host"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Host *</FormLabel>
                <FormControl>
                  <Input
                    placeholder="192.168.1.100"
                    data-testid="device-host-input"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {!isGeneric && (
            <FormField
              control={form.control}
              name="port"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Port</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder={String(defaultPort)}
                      data-testid="device-port-input"
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : undefined,
                        )
                      }
                    />
                  </FormControl>
                  <FormDescription>Default: {defaultPort}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        <FormField
          control={form.control}
          name="model"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Model</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Smart Videohub 40x40" {...field} />
              </FormControl>
              <FormDescription>
                Optional: specific device model for reference
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="management_url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Management URL</FormLabel>
              <FormControl>
                <Input
                  placeholder="https://192.168.1.100"
                  data-testid="device-management-url-input"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                Optional: URL for the device&apos;s web management interface
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="enabled"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel>Auto-connect</FormLabel>
                <FormDescription>
                  Automatically connect when bridge starts
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
            data-testid="device-form-submit"
          >
            {submitLabel ?? "Add Device"}
          </Button>
        </div>
      </form>
    </Form>
  );
};
